import { SXStyleSheet } from '@/utils';
import { typographyClasses } from '@mui/material';

import { bm } from '@/theme/bausteinMasse';

import { WEBSITE_STIL } from '@/constants/websiteStil';

const style = SXStyleSheet.create({
  container: {
    // Ecke, Mindesthoehe, Randbreite und Schrift aus `theme.bausteinMasse.knopf`
    // (Alt-Theme der Beratungsstrecke: Original-Werte, theme/streckeAlt).
    borderRadius: (theme) => bm(theme).knopf.radius,
    textTransform: 'none',
    width: '100%',
    padding: '6px 14px',
    height: 'auto',
    // Design-Test 25.10.2026: einheitlich 36px, kein Schatten.
    minHeight: (theme) => bm(theme).knopf.minHoehe,
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
    [`&.Mui-disabled`]: {
      backgroundColor: (theme) => theme.palette.grey[50],
      cursor: 'not-allowed',
    },
  },
  autoWidthContainer: {
    width: 'fit-content',
  },
  progress: {
    color: 'primary.contrastText',
  },
  iconContainer: {
    mr: 1,
  },
  // Gegenstueck zu iconContainer fuer iconPosition="end".
  iconContainerEnd: {
    ml: 1,
  },
  primary: {
    backgroundColor: 'primary.main',
    '&:hover': {
      backgroundColor: 'primary.dark',
    },
  },
  secondary: {
    backgroundColor: 'secondary.main',
    '&:hover': {
      // Gold-Hover aus dem Theme (DESIGN_SYSTEM.md „Buttons": hover #B08840).
      backgroundColor: 'secondary.dark',
    },
  },
  // Text-Link-Variante in Primaerfarbe, ohne Rahmen — „+ Kind hinzufuegen"
  // (Design-Paket 3, 11.09.2026). Ersetzt den rohen MUI-`Button` in
  // `TextButton`; wie `dark`/`website`/`danger` additiv, keine
  // Parallelkomponente (Decision #116).
  link: {
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    padding: '6px 10px',
    '&:hover': {
      backgroundColor: 'aktion.tealHauch',
      boxShadow: 'none',
    },
    // Reiner Text-Knopf: deaktiviert KEIN grauer Kasten aus `container`
    // (Design-Fix 19.10.2026, alle Varianten durchgesehen).
    '&.Mui-disabled': { backgroundColor: 'transparent' },
    [`& .${typographyClasses.button}`]: {
      color: 'primary.main',
      fontSize: 14,
      fontWeight: 500,
    },
  },
  tertiary: {
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    padding: '6px 10px',
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    // Ghost-Knopf: deaktiviert ohne grauen Kasten (Design-Fix 19.10.2026).
    '&.Mui-disabled': { backgroundColor: 'transparent' },
    [`& .${typographyClasses.button}`]: {
      color: 'text.secondary',
      fontSize: 14,
      fontWeight: 500,
    },
  },
  // CI-Variante in DIF-Dunkelblau (Upload-Landingpage, Auftrag Alex
  // 30.07.2026). Additiv, damit kein raw MUI Button und keine
  // Parallelkomponente entsteht (Decision #116).
  dark: {
    backgroundColor: 'primary.dark',
    '&:hover': {
      backgroundColor: 'primary.main',
    },
  },
  // Website-CI der oeffentlichen Kundenseiten (deutsche-foerderberatung.de,
  // Auftrag Alex 31.07.2026): Gold-Pille, Satoshi-Stack, tiefes Navy.
  // Additiv wie `dark` — kein raw MUI Button, keine Parallelkomponente.
  website: {
    backgroundColor: WEBSITE_STIL.gold,
    borderRadius: WEBSITE_STIL.pillenRadius,
    padding: '10px 24px',
    '&:hover': {
      backgroundColor: WEBSITE_STIL.goldHover,
    },
    // Deaktiviert bleibt GOLD mit reduzierter Deckkraft, nicht grau
    // (Nachbesserung Alex 31.07.2026 — Beispiel-Vorschau zeigt den CTA
    // sichtbar in Website-CI).
    '&.Mui-disabled': {
      backgroundColor: WEBSITE_STIL.gold,
      opacity: 0.55,
    },
    [`& .${typographyClasses.button}`]: {
      color: WEBSITE_STIL.navyTief,
      fontFamily: WEBSITE_STIL.schriftText,
      fontWeight: 700,
      lineHeight: WEBSITE_STIL.textHoehe,
    },
  },
  // Design-Test Stufe 2b (25.10.2026): Sekundaer-Aktion wieder mit Teal-Rand
  // und Teal-Schrift wie Original; Rand 1px statt 1.5px (flache Form).
  outlined: (theme) => ({
    backgroundColor: bm(theme).knopf.umrandetFlaeche ?? theme.palette.background.paper,
    border: `${bm(theme).knopf.randBreite} solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: bm(theme).knopf.umrandetHover ?? theme.palette.primary.hover,
      border: `${bm(theme).knopf.randBreite} solid ${theme.palette.primary.main}`,
    },
    [`& .${typographyClasses.button}`]: {
      color: theme.palette.primary.main,
    },
  }),
  // Umrandet auf DUNKLEM Grund (Wizard-Kopf, Entscheid Alex 02.10.2026):
  // durchscheinend, weisser Rand, weisse Schrift — dieselben Werte wie die
  // Plakette `BuchungsmodusBadge variante="kopf"` daneben, damit Knopf und
  // Plakette als ein Stil lesen. `outlined` ist fuer helle Flaechen gebaut
  // (primary-Rand/-Schrift) und auf `primary.darker` kaum sichtbar. Additiv
  // wie `dark`/`danger`: kein raw MUI Button, keine Parallelkomponente.
  // Gesperrt bleibt er durchscheinend statt des hellen `grey.50`-Kastens aus
  // `container`, der auf dunklem Grund als Fremdkoerper staende.
  outlinedOnDark: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.22)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.16)',
      border: '1px solid rgba(255,255,255,0.4)',
    },
    // Deaktiviert: Rand und WEISSE Schrift bleiben, nur gedaempft — auf dem
    // dunklen Kopf muss er lesbar bleiben (design-system.md §2). Die helle
    // Deaktiviert-Optik `disabledText` gilt hier ausdruecklich NICHT
    // (Design-Fix 19.10.2026: sie setzte dunkle Schrift auf dunklen Grund).
    '&.Mui-disabled': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.22)',
      opacity: 0.6,
    },
    [`& .${typographyClasses.button}`]: {
      color: 'common.white',
    },
  },
  // Umrandet wie `outlined`, aber in Warnfarbe — fuer Aktionen, die etwas
  // wegnehmen (Kind entfernen). Additiv wie `dark` und `website`: kein raw
  // MUI Button, keine Parallelkomponente (Decision #116). Farbe aus der
  // Palette statt als Hex, damit sie einem Theme-Wechsel folgt.
  danger: (theme) => ({
    backgroundColor: 'transparent',
    border: `${bm(theme).knopf.randBreite} solid ${theme.palette.error.main}`,
    '&:hover': {
      backgroundColor: theme.palette.error.light,
      border: `${bm(theme).knopf.randBreite} solid ${theme.palette.error.main}`,
    },
    [`& .${typographyClasses.button}`]: {
      color: theme.palette.error.main,
    },
  }),
  normal: {
    padding: '6px 14px',
  },
  small: {
    padding: '4px 10px',
    minHeight: (theme) => bm(theme).knopf.minHoeheKlein,
  },
  large: {
    padding: '8px 20px',
  },
  disabled: {
    cursor: 'not-allowed !important',
    pointerEvents: 'auto',
    '&.Mui-disabled': {
      cursor: 'not-allowed !important',
      pointerEvents: 'auto',
    },
  },
  // 🔥 LESBARKEIT IM DEAKTIVIERTEN ZUSTAND (14.10.2026, Optik-Durchgang
  // Vertriebsservice-Portal). Der Container setzt deaktiviert `grey[50]`
  // (#EEEEEE) als Hintergrund — die Schrift blieb aber die der Variante, bei
  // primary/secondary/dark also WEISS. Weiß auf #EEEEEE war praktisch
  // unsichtbar („Zoom beitreten", „Bereich speichern"). Jetzt gedämpfte
  // Textfarbe, und umrandete Varianten verlieren ihre Akzentkante.
  // Nicht bei `website`: die bleibt deaktiviert bewusst Gold (Alex 31.07.2026).
  // Nicht bei `outlinedOnDark`: dunkler Grund, eigene Deaktiviert-Optik
  // (Design-Fix 19.10.2026) — siehe dort.
  disabledText: {
    '&.Mui-disabled': {
      borderColor: 'divider',
    },
    [`&.Mui-disabled .${typographyClasses.button}`]: {
      color: 'text.primary',
      opacity: 0.6,
    },
  },
});

export default style;
